<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { gql } from "@urql/core";
import { useMutation, useQuery } from "@urql/vue";
import { IconUpload } from "@tabler/icons-vue";
import { token } from "@/init";
import FormDialog from "@/components/common/FormDialog.vue";

type UploadConfig = {
  id: string;
  uploadsEnabled: boolean;
};

const props = defineProps<{
  templateId: string;
  classicTheme?: boolean;
  templateName: string | null;
}>();

const emit = defineEmits<{
  saved: [];
}>();

const { t } = useI18n();

const dialogOpen = ref(false);
const deactivateOpen = ref(false);
const saving = ref(false);
const errorMessage = ref("");
const refreshErrorMessage = ref("");

type UploadConfigQuery = {
  documentSpaceConfig?: UploadConfig | null;
};

const UploadConfigDocument = gql`
  query HcpUploadTemplateConfig($id: BigInt!) {
    documentSpaceConfig(id: $id) {
      id
      uploadsEnabled
    }
  }
`;
const ConfigureUploadsDocument = gql`
  mutation ConfigureHcpUploads($configId: BigInt!, $uploadsEnabled: Boolean!) {
    configureHcpUploads(
      input: { pConfigId: $configId, pUploadsEnabled: $uploadsEnabled }
    ) {
      configuredConfig {
        id
        uploadsEnabled
      }
    }
  }
`;

const uploadConfigQuery = useQuery<UploadConfigQuery>({
  query: UploadConfigDocument,
  variables: computed(() => ({ id: props.templateId })),
  pause: computed(() => !token.value),
  requestPolicy: "cache-and-network",
});

const queriedConfig = computed(
  () => uploadConfigQuery.data.value?.documentSpaceConfig ?? null,
);
const retainedConfig = ref<UploadConfig | null>(null);
const committedConfig = ref<UploadConfig | null>(null);
watch(
  [() => props.templateId, queriedConfig],
  ([templateId, nextConfig], previous) => {
    if (previous && previous[0] !== templateId) retainedConfig.value = null;
    if (nextConfig?.id === templateId) retainedConfig.value = nextConfig;
  },
  { immediate: true },
);
const config = computed(() =>
  committedConfig.value?.id === props.templateId
    ? committedConfig.value
    : uploadConfigQuery.error.value
      ? null
      : queriedConfig.value?.id === props.templateId
        ? queriedConfig.value
        : retainedConfig.value?.id === props.templateId
          ? retainedConfig.value
          : null,
);
const configIsCurrent = computed(
  () =>
    queriedConfig.value?.id === props.templateId &&
    !uploadConfigQuery.fetching.value &&
    !uploadConfigQuery.error.value &&
    !refreshErrorMessage.value,
);
const { executeMutation: configureUploads } = useMutation(
  ConfigureUploadsDocument,
);
function clearDraft() {
  dialogOpen.value = false;
  deactivateOpen.value = false;
  errorMessage.value = "";
}

function applyCommittedConfig(nextState: UploadConfig) {
  if (nextState.id !== props.templateId) return;
  retainedConfig.value = nextState;
  committedConfig.value = nextState;
}

function openDialog() {
  if (!config.value || !configIsCurrent.value || saving.value) return;
  errorMessage.value = "";
  refreshErrorMessage.value = "";
  dialogOpen.value = true;
}

async function loadServerConfig(operationTemplateId: string) {
  if (props.templateId !== operationTemplateId) return;
  const refreshed = await uploadConfigQuery.executeQuery({
    requestPolicy: "network-only",
  });
  if (props.templateId !== operationTemplateId) return;
  if (refreshed.error.value) throw refreshed.error.value;
  const serverConfig = refreshed.data.value?.documentSpaceConfig;
  if (!serverConfig || serverConfig.id !== operationTemplateId) {
    throw new Error("HCP upload configuration refresh returned stale data");
  }
  retainedConfig.value = serverConfig;
  committedConfig.value = null;
  return serverConfig;
}

async function reconcileFromServer(operationTemplateId: string) {
  const serverConfig = await loadServerConfig(operationTemplateId);
  if (serverConfig) errorMessage.value = "";
}

watch(
  () => props.templateId,
  () => {
    committedConfig.value = null;
    clearDraft();
    refreshErrorMessage.value = "";
  },
);

watch(
  () => uploadConfigQuery.error.value,
  (queryError) => {
    if (!queryError) return;
    retainedConfig.value = null;
    clearDraft();
  },
);

async function setUploadsEnabled(enabled: boolean) {
  if (saving.value || !configIsCurrent.value) return;
  const operationTemplateId = props.templateId;
  saving.value = true;
  errorMessage.value = "";
  refreshErrorMessage.value = "";
  try {
    try {
      const result = await configureUploads({
        configId: operationTemplateId,
        uploadsEnabled: enabled,
      });
      if (result.error) throw result.error;
      const configuredConfig = result.data?.configureHcpUploads
        ?.configuredConfig as UploadConfig | null | undefined;
      if (configuredConfig) applyCommittedConfig(configuredConfig);
    } catch (error) {
      console.error("Saving HCP upload settings failed", error);
      let reconciled = false;
      try {
        await reconcileFromServer(operationTemplateId);
        reconciled = true;
      } catch (reconcileError) {
        console.error("Reloading HCP upload settings failed", reconcileError);
      }
      if (props.templateId === operationTemplateId) {
        errorMessage.value = t(
          reconciled
            ? "templateEditor.toolbar.settings.hcpUploads.saveReconciledError"
            : "templateEditor.toolbar.settings.hcpUploads.saveReconciliationFailedError",
        );
      }
      return;
    }
    if (props.templateId !== operationTemplateId) return;
    clearDraft();
    try {
      await loadServerConfig(operationTemplateId);
    } catch (reloadError) {
      console.error(
        "Reloading HCP upload settings after save failed",
        reloadError,
      );
      refreshErrorMessage.value = t(
        "templateEditor.toolbar.settings.hcpUploads.refreshError",
      );
    }
    emit("saved");
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <section
    v-if="config && config.id === templateId"
    data-test="hcp-upload-settings"
    class="tw:border-t tw:border-separator tw:pt-4"
  >
    <div class="tw:flex tw:items-start tw:gap-3">
      <div
        class="tw:flex tw:h-8 tw:w-8 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-lg tw:bg-pv-turquoise-500/10 tw:text-pv-turquoise-600"
      >
        <IconUpload class="tw:h-4 tw:w-4" :stroke-width="1.7" />
      </div>
      <div class="tw:min-w-0 tw:flex-1">
        <div class="tw:flex tw:items-center tw:gap-2">
          <span class="tw:text-sm tw:font-semibold tw:text-label">
            {{ t("templateEditor.toolbar.settings.hcpUploads.title") }}
          </span>
          <span
            v-if="config.uploadsEnabled"
            class="tw:rounded-full tw:bg-pv-turquoise-500/10 tw:px-2 tw:py-0.5 tw:text-[10px] tw:font-semibold tw:text-pv-turquoise-600"
          >
            {{ t("templateEditor.toolbar.settings.hcpUploads.active") }}
          </span>
        </div>
        <p
          class="tw:m-0 tw:mt-1 tw:text-xs tw:leading-relaxed tw:text-label-quaternary"
        >
          {{
            t("templateEditor.toolbar.settings.hcpUploads.managedDescription")
          }}
        </p>
        <div class="tw:mt-3 tw:flex tw:flex-wrap tw:gap-2">
          <button
            v-if="!config.uploadsEnabled"
            data-test="hcp-upload-open"
            :disabled="saving || !configIsCurrent"
            class="tw:rounded-lg tw:border-0 tw:bg-pv-turquoise-500 tw:px-3 tw:py-1.5 tw:text-xs tw:font-medium tw:text-white tw:cursor-pointer tw:hover:bg-pv-turquoise-600"
            @click="openDialog"
          >
            {{ t("templateEditor.toolbar.settings.hcpUploads.add") }}
          </button>
          <button
            v-if="config.uploadsEnabled"
            data-test="hcp-upload-open-deactivate"
            :disabled="saving || !configIsCurrent"
            class="tw:rounded-lg tw:border tw:border-separator-strong tw:bg-surface tw:px-3 tw:py-1.5 tw:text-xs tw:text-label-tertiary tw:cursor-pointer tw:hover:bg-hover"
            @click="
              errorMessage = '';
              deactivateOpen = true;
            "
          >
            {{ t("templateEditor.toolbar.settings.hcpUploads.deactivate") }}
          </button>
        </div>
        <p
          v-if="refreshErrorMessage"
          data-test="hcp-upload-refresh-error"
          role="alert"
          class="tw:m-0 tw:mt-2 tw:text-xs tw:text-destructive"
        >
          {{ refreshErrorMessage }}
        </p>
      </div>
    </div>

    <FormDialog
      :classic-theme="classicTheme"
      v-model="dialogOpen"
      :title="t('templateEditor.toolbar.settings.hcpUploads.dialogTitle')"
      :submit-label="t('templateEditor.toolbar.settings.hcpUploads.activate')"
      :submit-disabled="saving || !configIsCurrent"
      :scrollable="true"
      :close-on-backdrop="!saving"
      :close-disabled="saving"
      @submit="setUploadsEnabled(true)"
    >
      <div class="tw:flex tw:flex-col tw:gap-4">
        <p
          class="tw:m-0 tw:text-xs tw:text-label-quaternary"
          data-test="hcp-upload-managed-destination"
        >
          {{
            t("templateEditor.toolbar.settings.hcpUploads.managedDescription")
          }}
        </p>

        <p
          v-if="errorMessage"
          data-test="hcp-upload-save-error"
          role="alert"
          class="tw:m-0 tw:rounded-lg tw:bg-destructive/10 tw:p-3 tw:text-xs tw:text-destructive"
        >
          {{ errorMessage }}
        </p>
      </div>
    </FormDialog>

    <FormDialog
      :classic-theme="classicTheme"
      v-model="deactivateOpen"
      :title="t('templateEditor.toolbar.settings.hcpUploads.deactivateTitle')"
      :message="
        t('templateEditor.toolbar.settings.hcpUploads.deactivateMessage')
      "
      :submit-label="t('templateEditor.toolbar.settings.hcpUploads.deactivate')"
      :submit-disabled="saving || !configIsCurrent"
      submit-variant="danger"
      :close-on-backdrop="!saving"
      :close-disabled="saving"
      @submit="setUploadsEnabled(false)"
    >
      <p
        v-if="errorMessage"
        data-test="hcp-upload-deactivate-error"
        role="alert"
        class="tw:m-0 tw:text-xs tw:text-destructive"
      >
        {{ errorMessage }}
      </p>
    </FormDialog>
  </section>
</template>
